import GoogleIcon from '@mui/icons-material/Google';
import { AppBar, Box, Button, Menu, MenuItem, Paper, Toolbar, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';
import { getBackendURL } from '../utils/backendUrl';

const languages = [
  { code: 'en', label: 'English', icon: '/flags/gb.svg' },
  { code: 'ua', label: 'Українська', icon: '/flags/ua.svg' },
  { code: 'ru', label: 'русский', icon: '/flags/kz.svg' },
  { code: 'pl', label: 'Polski', icon: '/flags/pl.svg' },
];

const LoginPage = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [language, setLanguage] = useState('en');
    const [langAnchor, setLangAnchor] = useState(null);

    useEffect(() => {
        const savedLanguage = localStorage.getItem('language') || 'en';
        setLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
    }, [i18n]);

    const handleLanguageChange = (newLanguage) => {
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);
        setLangAnchor(null);
    };

    const handleLogin = () => {
        const backendURL = getBackendURL();
        window.location.href = `${backendURL}/api/v1/auth/google/login`;
    };

    const currentLanguage = languages.find(l => l.code === language);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f5f5f5' }}>
            {/* Toolbar */}
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#fff', color: 'text.primary', boxShadow: '0 1px 8px 0 rgba(60,60,60,0.04)' }}>
                <Toolbar sx={{ minHeight: 56, px: { xs: 1, md: 3 }, display: 'flex', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Logo height={36} />
                        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'text.primary', ml: 1 }}>
                            Ringostat
                        </Typography>
                    </Box>

                    {/* Language Selector */}
                    <Box
                        onClick={(e) => setLangAnchor(e.currentTarget)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.7,
                            cursor: 'pointer',
                            bgcolor: 'background.default',
                            '&:hover': { bgcolor: 'action.hover' }
                        }}
                    >
                        <img
                            src={currentLanguage?.icon || '/flags/gb.svg'}
                            alt={currentLanguage?.label || 'English'}
                            style={{ width: 20, height: 20, marginRight: 8, verticalAlign: 'middle', borderRadius: '3px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentLanguage?.label || 'English'}
                        </Typography>
                    </Box>

                    <Menu
                        anchorEl={langAnchor}
                        open={Boolean(langAnchor)}
                        onClose={() => setLangAnchor(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: 2,
                                mt: 1,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                minWidth: 180,
                            }
                        }}
                    >
                        {languages.map(l => (
                            <MenuItem
                                key={l.code}
                                selected={l.code === language}
                                onClick={() => handleLanguageChange(l.code)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    minWidth: 160,
                                    py: 1.25,
                                    px: 2,
                                    '&:hover': {
                                        bgcolor: 'rgba(0, 200, 83, 0.08)'
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: 'rgba(0, 200, 83, 0.12)',
                                        '&:hover': {
                                            bgcolor: 'rgba(0, 200, 83, 0.16)'
                                        }
                                    }
                                }}
                            >
                                <img
                                    src={l.icon}
                                    alt={l.label}
                                    style={{
                                        width: 24,
                                        height: 24,
                                        verticalAlign: 'middle',
                                        borderRadius: '4px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                    }}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {l.label}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Login Form */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        bgcolor: 'white',
                        borderRadius: theme.spacing(1),
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: theme.shadows[2],
                        },
                        p: 7,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 3.5,
                        minWidth: { xs: '400px', sm: '550px' },
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', sm: '2.5rem' } }}>
                        {t('login.title', 'Ringostat SSO')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 400 }}>
                        {t('login.welcome', 'Welcome to Ringostat Single Sign-On')}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<GoogleIcon sx={{ fontSize: 24 }} />}
                        onClick={handleLogin}
                        sx={{
                            bgcolor: '#00C853',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '1.1rem',
                            '&:hover': {
                                bgcolor: '#00A040',
                            },
                            mt: 2,
                            py: 2,
                            px: 4,
                        }}
                    >
                        {t('login.continueWithGoogle', 'Continue with Google')}
                    </Button>
                </Paper>
            </Box>
        </Box>
    );
};

export default LoginPage;
